import { NoSymbolIcon } from "@heroicons/react/24/outline";

/** @typedef {'vendor' | 'sponsor' | 'user'} ActivateOrDeactivateEntityType */

const TYPE_COPY = {
  sponsor: {
    titleNoun: "Sponsor Account",
    bodyReactivate: (name) =>
      `Are you sure you want to reactivate ${name}? The account status will be changed to Active.`,
    bodyDeactivate: (name) =>
      `Are you sure you want to deactivate ${name}? The account status will be changed to Inactive.`,
  },
  vendor: {
    titleNoun: "Vendor Account",
    bodyReactivate: (name) =>
      `Are you sure you want to reactivate ${name}? The account status will be changed to Active.`,
    bodyDeactivate: (name) =>
      `Are you sure you want to deactivate ${name}? The account status will be changed to Inactive.`,
  },
  user: {
    titleNoun: "Account",
    bodyReactivate: (name) =>
      `Are you sure you want to reactivate ${name}? The account status will be changed to Active.`,
    bodyDeactivate: (name) =>
      `Are you sure you want to deactivate ${name}? The account status will be changed to Inactive.`,
  },
};

/**
 * Confirmation modal to activate or deactivate an entity (vendor, sponsor, or user).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {ActivateOrDeactivateEntityType} props.type
 * @param {boolean} props.isDeactivated - When true, the flow reactivates; when false, deactivates.
 * @param {string} props.entityName - Display name shown in the message body.
 */
export default function ActivateOrDeactivateConfirmationModal({
  open,
  onClose,
  onConfirm,
  type = "sponsor",
  isDeactivated,
  entityName,
}) {
  if (!open) return null;

  const copy = TYPE_COPY[type] ?? TYPE_COPY.sponsor;
  const actionVerb = isDeactivated ? "Reactivate" : "Deactivate";
  const body = isDeactivated
    ? copy.bodyReactivate(entityName ?? "")
    : copy.bodyDeactivate(entityName ?? "");

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="activate-deactivate-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
              <NoSymbolIcon className="h-6 w-6 text-orange-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3
                className="text-base font-semibold leading-6 text-gray-900"
                id="activate-deactivate-modal-title"
              >
                {actionVerb} {copy.titleNoun}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">{body}</p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
              onClick={onConfirm}
            >
              {actionVerb}
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
